import type { LucideIcon } from "lucide-react";
import { useId } from "react";

import classes from "./GradientIcon.module.css";

export interface GradientIconProps {
  icon: LucideIcon;
  size: number;
  /** Fill the shape with the gradient too, not only its outline (the credits pill's Zap). */
  filled?: boolean;
  strokeWidth?: number;
  /** The gradient's ends as CSS colors; the accent tokens when absent. */
  colors?: { from: string; to: string };
}

/**
 * A lucide icon drawn in a gradient: the accent (`--cai-gradient-from` to `--cai-gradient-to`), or
 * the two colors given, so an icon can match the text beside it.
 */
export function GradientIcon(props: GradientIconProps) {
  const { icon: Icon, size, filled = false, strokeWidth, colors } = props;
  // React's ids hold punctuation that an SVG `url(#...)` reference does not take.
  const id = `cai-gradient-${useId().replace(/[^\w-]/g, "")}`;
  const paint = `url(#${id})`;
  return (
    <>
      <svg width={0} height={0} aria-hidden className={classes.defs}>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop
            offset="0"
            className={classes.from}
            style={colors === undefined ? undefined : { stopColor: colors.from }}
          />
          <stop
            offset="1"
            className={classes.to}
            style={colors === undefined ? undefined : { stopColor: colors.to }}
          />
        </linearGradient>
      </svg>
      <Icon
        size={size}
        stroke={paint}
        fill={filled ? paint : "none"}
        aria-hidden
        {...(strokeWidth === undefined ? {} : { strokeWidth })}
      />
    </>
  );
}
